import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api.js';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../context/AuthContext.js';
import {
  BarChart3, Building2, TrendingUp, TestTube, Shield, X, Plug, Bot, Pencil
} from 'lucide-react';
import LoadingPopup from '../misc/Loading.js';
import Slider from '../../components/Slider.js';

const ApplicationHomepage = () => {
  const [toasts, setToasts] = useState([]);

  // Toast utilities
  const addErrorToast = (message) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type: "error" }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  };

  const addSuccessToast = (message) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type: "success" }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  };

  const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  // State & refs
  const modalRef = useRef(null);
  const { role, session } = useAuth();
  const token = session?.access_token;
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [scanBreakdown, setScanBreakdown] = useState({});
  const [hoveredScan, setHoveredScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [applicationName, setApplicationName] = useState('');
  const [businessOwner, setBusinessOwner] = useState('');
  const [confidentiality, setConfidentiality] = useState(50);
  const [integrity, setIntegrity] = useState(50);
  const [availability, setAvailability] = useState(50);
  const [societalImpact, setSocietalImpact] = useState(50);
  const [userAuthority, setUserAuthority] = useState('non-authenticated');
  const [privilegeLevel, setPrivilegeLevel] = useState('low');
  const [hasMcp, setHasMcp] = useState(false);
  const [hasAgenticWorkflow, setHasAgenticWorkflow] = useState(false);
  const [networkExposure, setNetworkExposure] = useState('public');

  // Edit modal state
  const editModalRef = useRef(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editAssetName, setEditAssetName] = useState('');
  const [editBusinessOwner, setEditBusinessOwner] = useState('');
  const [editConfidentiality, setEditConfidentiality] = useState(50);
  const [editIntegrity, setEditIntegrity] = useState(50);
  const [editAvailability, setEditAvailability] = useState(50);
  const [editSocietalImpact, setEditSocietalImpact] = useState(50);
  const [editUserAuthority, setEditUserAuthority] = useState('non-authenticated');
  const [editPrivilegeLevel, setEditPrivilegeLevel] = useState('low');
  const [editHasMcp, setEditHasMcp] = useState(false);
  const [editHasAgenticWorkflow, setEditHasAgenticWorkflow] = useState(false);
  const [editNetworkExposure, setEditNetworkExposure] = useState('public');

  // Reverse-map a stored MPR/privileges_required value to authority + privilege radio state
  const mprToAuthority = (mpr) => {
    if (mpr === null || mpr === undefined) return { authority: 'non-authenticated', privilege: 'low' };
    const v = Number(mpr);
    if (v === 0.85) return { authority: 'non-authenticated', privilege: 'low' };
    if (v === 0.27) return { authority: 'authenticated', privilege: 'high' };
    return { authority: 'authenticated', privilege: 'low' };
  };

  const getEditAuthorityScore = () => {
    if (editUserAuthority === 'non-authenticated') return 0.85;
    return editPrivilegeLevel === 'low' ? 0.63 : 0.27;
  };

  // Calculate authority score based on user authority level
  const getAuthorityScore = () => {
    if (userAuthority === 'non-authenticated') {
      return 0.85;
    } else if (userAuthority === 'authenticated') {
      return privilegeLevel === 'low' ? 0.63 : 0.27;
    }
    return 0.85; // default
  };

  const calculateCriticality = (confidentiality, integrity, availability, societal) => {
    const sum = confidentiality + integrity + availability + societal;
    if (sum > 340) {
      return "Critical";
    } else if (sum > 220) {
      return "High";
    } else if (sum > 88) {
      return "Medium";
    } else {
      return "Low";
    }
  };

  // Data fetch
  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const [assetRes, breakdownRes] = await Promise.allSettled([
        API.get(`${process.env.REACT_APP_DJANGO_API_URL}/all-asset-data/`),
        API.get(`${process.env.REACT_APP_DJANGO_API_URL}/scan-breakdown/`),
      ]);
      if (assetRes.status === 'fulfilled') setData(assetRes.value.data);
      else setError("An unexpected error occurred");
      if (breakdownRes.status === 'fulfilled') setScanBreakdown(breakdownRes.value.data.breakdown || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Modal close on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setModalOpen(false);
      }
    };
    if (modalOpen) document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [modalOpen]);

  // Edit modal close on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (editModalRef.current && !editModalRef.current.contains(event.target)) {
        setEditModalOpen(false);
      }
    };
    if (editModalOpen) document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [editModalOpen]);

  // Open edit modal pre-filled from the existing asset row
  const handleEditOpen = (index) => {
    if (!data) return;
    setEditAssetName(data.assetNameList[index] || '');
    setEditBusinessOwner(data.businessOwnerList?.[index] || '');
    setEditConfidentiality(Math.round(data.confidentialityRequirementList?.[index] ?? 50));
    setEditIntegrity(Math.round(data.integrityRequirementList?.[index] ?? 50));
    setEditAvailability(Math.round(data.availabilityRequirementList?.[index] ?? 50));
    setEditSocietalImpact(Math.round(data.societalImpactRequirementList?.[index] ?? 50));
    const { authority, privilege } = mprToAuthority(data.privilegesRequiredList?.[index]);
    setEditUserAuthority(authority);
    setEditPrivilegeLevel(privilege);
    setEditHasMcp(Boolean(data.hasMcpList?.[index]));
    setEditHasAgenticWorkflow(Boolean(data.hasAgenticList?.[index]));
    setEditNetworkExposure(data.networkExposureList?.[index] || 'public');
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editBusinessOwner) {
      addErrorToast("Business Owner is required");
      return;
    }
    setEditModalOpen(false);
    setLoading(true);
    const payload = {
      asset_name: editAssetName,
      business_owner: editBusinessOwner,
      confidentiality_requirement: editConfidentiality,
      integrity_requirement: editIntegrity,
      availability_requirement: editAvailability,
      societal_impact_requirement: editSocietalImpact,
      authority_score: getEditAuthorityScore(),
      has_mcp: editHasMcp,
      has_agentic_workflow: editHasAgenticWorkflow,
      network_exposure: editNetworkExposure,
    };
    try {
      if (!token) return;
      await API.post(
        `${process.env.REACT_APP_DJANGO_API_URL}/edit-asset/`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchData();
      addSuccessToast("Application updated successfully.");
    } catch (err) {
      const errMsg = err.response?.data?.error
        || err.response?.data?.message
        || err.message
        || "Failed to update application.";
      addErrorToast(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const handleredirect = (name) => {
    sessionStorage.setItem('selectedApp', name)
    if (data?.assetNameList) {
      const idx = data.assetNameList.indexOf(name)
      if (idx !== -1) {
        sessionStorage.setItem('selectedAppHasMcp', String(Boolean(data.hasMcpList?.[idx])))
        sessionStorage.setItem('selectedAppHasAgentic', String(Boolean(data.hasAgenticList?.[idx])))
        if (data.assetIdList?.[idx]) {
          sessionStorage.setItem('selectedAppId', data.assetIdList[idx])
        }
      }
    }
    navigate("/dashboard")
  }

  // Delete handler
  const handleDelete = async (name, scan_count) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${name}" application? You have ${scan_count} scans associated with this application.`
    );
    if (confirmDelete) {
      setLoading(true);
      try {
        const response = await API.post(
          `${process.env.REACT_APP_DJANGO_API_URL}/delete-asset/`,
          { asset_name: name }
        );
        if (response.status === 200 && response.data?.success) {
          addSuccessToast(response.data.success);
          await Promise.all([fetchData()]);
        } else {
          const errMsg = response.data?.error || "Failed to delete application.";
          addErrorToast(errMsg);
        }
      } catch (error) {
        const errMsg =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "An unexpected error occurred.";
        addErrorToast(errMsg);
      } finally {
        setLoading(false);
      }
    }
  };

  // Add/submit handler
  const send_data = async (appData) => {
    try {
      if (!token) return;
      await API.post(
        `${process.env.REACT_APP_DJANGO_API_URL}/add-asset/`,
        appData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      throw err;
    }
  };
  const handleSubmit = async () => {
    if (!applicationName || !businessOwner) {
      addErrorToast("All fields are required");
      return;
    }
    setModalOpen(false);
    setLoading(true);
    const appData = {
      asset_name: applicationName,
      business_owner: businessOwner,
      confidentiality_requirement: confidentiality,
      integrity_requirement: integrity,
      availability_requirement: availability,
      societal_impact_requirement: societalImpact,
      authority_score: getAuthorityScore(),
      has_mcp: hasMcp,
      has_agentic_workflow: hasAgenticWorkflow,
      network_exposure: networkExposure,
    };
    try {
      await send_data(appData);
      await Promise.all([fetchData()]);
      setApplicationName('');
      setBusinessOwner('');
      setConfidentiality(50);
      setIntegrity(50);
      setAvailability(50);
      setSocietalImpact(50);
      setUserAuthority('non-authenticated');
      setPrivilegeLevel('low');
      setHasMcp(false);
      setHasAgenticWorkflow(false);
      setNetworkExposure('public');
      addSuccessToast("Application added successfully.");
    } catch (err) {
      const errMsg = err.response?.data?.error
        || err.response?.data?.message
        || err.message
        || "Failed to add application.";
      addErrorToast(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // Scan/asset counts
  const scanCount = data && Array.isArray(data.scansRunList)
    ? data.scansRunList.reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0)
    : 0;
  const assetCount = data?.assetNameList?.length || 0;
  const mcpAssetCount = Array.isArray(data?.hasMcpList)
    ? data.hasMcpList.filter(Boolean).length
    : 0;
  const agenticAssetCount = Array.isArray(data?.hasAgenticList)
    ? data.hasAgenticList.filter(Boolean).length
    : 0;

  // Modal open utility
  const openModal = () => {
    setModalOpen(true);
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Animated toasts */}
      <div className="fixed top-5 right-5 z-[1000] flex flex-col space-y-2 max-w-[calc(100vw-2.5rem)]">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              px-4 py-3 rounded-lg flex items-center gap-3 shadow-lg animate-slide-in
              border backdrop-blur-xl transition min-w-[240px]
              ${toast.type === "error"
                ? "bg-red-400/80 border-red-200 text-red-900"
                : "bg-green-500/20 border-green-400/80 text-green-400"}
            `}
          >
            <span className="flex-1 text-sm">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className={`hover:text-black text-lg font-bold focus:outline-none flex-shrink-0 ${
                toast.type === "error" ? "text-red-900" : "text-green-900"
              }`}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Page heading */}
      <p className="text-white font-bold text-2xl sm:text-3xl mb-6 mt-2 px-2">Application Dashboard</p>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
        <div className="bg-[#101012] border border-[#232325] rounded-2xl">
          <div className="flex flex-row justify-between items-center">
            <p className="text-white text-base sm:text-lg m-4 ml-6">Total Scans</p>
            <BarChart3 className="text-blue-400 w-7 h-7 m-4" />
          </div>
          <p className="font-bold text-4xl sm:text-5xl ml-6 text-white mb-6">{scanCount}</p>
        </div>
        <div className="bg-[#101012] border border-[#232325] rounded-2xl">
          <div className="flex flex-row justify-between items-center">
            <p className="text-white text-base sm:text-lg m-4 ml-6">Total Assets</p>
            <Building2 className="text-green-400 w-7 h-7 m-4" />
          </div>
          <p className="font-bold text-4xl sm:text-5xl ml-6 text-white mb-6">{assetCount}</p>
        </div>
        <div className="bg-[#101012] border border-[#232325] rounded-2xl">
          <div className="flex flex-row justify-between items-center">
            <p className="text-white text-base sm:text-lg m-4 ml-6">MCP Enabled</p>
            <Plug className="text-indigo-400 w-7 h-7 m-4" />
          </div>
          <p className="font-bold text-4xl sm:text-5xl ml-6 text-white mb-6">{mcpAssetCount}</p>
        </div>
        <div className="bg-[#101012] border border-[#232325] rounded-2xl">
          <div className="flex flex-row justify-between items-center">
            <p className="text-white text-base sm:text-lg m-4 ml-6">Agentic Workflows</p>
            <Bot className="text-purple-400 w-7 h-7 m-4" />
          </div>
          <p className="font-bold text-4xl sm:text-5xl ml-6 text-white mb-6">{agenticAssetCount}</p>
        </div>
      </div>

      {/* Main global loading overlay */}
      {loading && !modalOpen && (
        <LoadingPopup isVisible={true} />
      )}

      {/* Application Table — horizontally scrollable on small screens */}
      {!modalOpen && (loading ? null : error ? (
        <p className="text-red-400 px-2">{error}</p>
      ) : data ? (
        <div className="overflow-x-auto rounded-xl border border-[#232325] mb-6">
          <table className="w-full text-white text-sm border-collapse min-w-[640px]">
            <thead>
              <tr className="bg-[#232325] text-left">
                <th className="px-4 py-3 font-semibold text-gray-300">S.No</th>
                <th className="px-4 py-3 font-semibold text-gray-300 text-center">Application Name</th>
                <th className="px-4 py-3 font-semibold text-gray-300 text-center">Business Owner</th>
                <th className="px-4 py-3 font-semibold text-gray-300 text-center">Business Criticality</th>
                <th className="px-4 py-3 font-semibold text-gray-300 text-center">Created At</th>
                <th className="px-4 py-3 font-semibold text-gray-300 text-center">Scans Run</th>
                <th className="px-4 py-3 font-semibold text-gray-300 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.assetNameList && data.assetNameList.length > 0 ? (
                (() => {
                  const indexedAssets = data.assetNameList.map((name, index) => {
                    const criticality = calculateCriticality(
                      data.confidentialityRequirementList[index],
                      data.integrityRequirementList[index],
                      data.availabilityRequirementList[index],
                      data.societalImpactRequirementList[index]
                    );
                    return { index, criticality };
                  });

                  const criticalityOrder = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
                  indexedAssets.sort((a, b) => criticalityOrder[a.criticality] - criticalityOrder[b.criticality]);

                  return indexedAssets.map(({ index, criticality }, displayIndex) => {
                    const name = data.assetNameList[index];
                    return (
                      <tr key={index} className="hover:bg-[#101012] transition-colors">
                        <td className="px-4 py-3 border-b border-[#232325] text-gray-400">{displayIndex + 1}</td>
                        <td className="px-4 py-3 border-b border-[#232325] text-center">
                          <div className="flex items-center justify-center gap-2 flex-wrap">
                            <span>{name}</span>
                            {data.hasMcpList && data.hasMcpList[index] && (
                              <span
                                title="Uses Model Context Protocol"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-500/15 border border-indigo-400/40 text-indigo-300 text-[10px] font-medium"
                              >
                                <Plug className="w-3 h-3" />
                                MCP
                              </span>
                            )}
                            {data.hasAgenticList && data.hasAgenticList[index] && (
                              <span
                                title="Uses Agentic Workflows"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-500/15 border border-purple-400/40 text-purple-300 text-[10px] font-medium"
                              >
                                <Bot className="w-3 h-3" />
                                Agent
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b border-[#232325] text-center text-gray-300">
                          {data.businessOwnerList && data.businessOwnerList[index]}
                        </td>
                        <td className="px-4 py-3 border-b border-[#232325] text-center">
                          <span className={`inline-block min-w-[90px] px-3 py-1 rounded-xl font-semibold text-xs
                            transition-all duration-300 border
                            ${
                              criticality === 'Critical' ?
                              'bg-red-700/20 border-red-500/60 text-red-500' :
                              criticality === 'High' ?
                                'bg-red-500/20 border-red-400/60 text-red-400' :
                                criticality === 'Medium' ?
                                  'bg-yellow-500/20 border-yellow-400/60 text-yellow-400' :
                                  criticality === 'Low' ?
                                    'bg-green-500/20 border-green-400/60 text-green-400' :
                                    'bg-blue-500/20 border-blue-400/40 text-blue-300'
                            }`}
                          >
                            {criticality}
                          </span>
                        </td>
                        <td className="px-4 py-3 border-b border-[#232325] text-center text-gray-300">
                          {data.createdAtList && data.createdAtList[index]}
                        </td>
                        <td className="px-4 py-3 border-b border-[#232325] text-center text-gray-300">
                          <span
                            className="cursor-default underline decoration-dotted decoration-gray-500"
                            onMouseEnter={(e) => {
                              if (!scanBreakdown[name]) return;
                              const rect = e.currentTarget.getBoundingClientRect();
                              setHoveredScan({ name, x: rect.left + rect.width / 2, y: rect.top });
                            }}
                            onMouseLeave={() => setHoveredScan(null)}
                          >
                            {data.scansRunList && data.scansRunList[index]}
                          </span>
                        </td>
                        <td className="px-4 py-3 border-b border-[#232325] text-center">
                          <div className="flex justify-center items-center gap-2">
                            <button
                              className="bg-green-500/20 border border-green-400/60 text-green-400 rounded-xl cursor-pointer px-3 py-1.5 text-xs font-medium hover:bg-green-500/30 transition-colors"
                              onClick={() => handleredirect(name)}
                            >
                              View Dashboard
                            </button>
                            {role === 'client_admin' && (
                              <>
                                <button
                                  onClick={() => handleEditOpen(index)}
                                  className="bg-blue-500/20 border border-blue-400/60 text-blue-400 rounded-xl cursor-pointer p-1.5 hover:bg-blue-500/30 transition-colors"
                                  title="Edit Application"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(name, data.scansRunList[index])}
                                  className="bg-red-500/20 border border-red-400/60 text-red-400 rounded-xl cursor-pointer p-1.5 hover:bg-red-500/30 transition-colors"
                                  title="Delete Application"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })()
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-400">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-400 px-2">No data found</p>
      ))}

      {/* Scan breakdown tooltip — fixed position, escapes table overflow */}
      {hoveredScan && scanBreakdown[hoveredScan.name] && (
        <div
          className="pointer-events-none"
          style={{
            position: 'fixed',
            left: hoveredScan.x,
            top: hoveredScan.y,
            transform: 'translate(-50%, calc(-100% - 10px))',
            zIndex: 9999,
          }}
        >
          <div className="bg-[#18181b] border border-[#2e2e32] rounded-xl p-3 shadow-2xl w-52 text-left">
            <p className="text-gray-400 text-[10px] uppercase tracking-widest font-semibold mb-2">Scan Breakdown</p>
            {[
              { label: 'Redteam Scans',   value: scanBreakdown[hoveredScan.name].redteam,       color: 'text-red-400' },
              { label: 'AgentOps SAST',   value: scanBreakdown[hoveredScan.name].agentops_sast,  color: 'text-blue-400' },
              { label: 'AgentOps DAST',   value: scanBreakdown[hoveredScan.name].agentops_dast,  color: 'text-purple-400' },
              { label: 'MCP Scanner SAST',value: scanBreakdown[hoveredScan.name].mcp_sast,       color: 'text-yellow-400' },
              { label: 'MCP Scanner DAST',value: scanBreakdown[hoveredScan.name].mcp_dast,       color: 'text-emerald-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex items-center justify-between py-0.5">
                <span className="text-gray-300 text-xs">{label}</span>
                <span className={`text-xs font-semibold ml-3 ${color}`}>{value}</span>
              </div>
            ))}
          </div>
          <div className="w-2.5 h-2.5 bg-[#18181b] border-r border-b border-[#2e2e32] rotate-45 mx-auto -mt-1.5" />
        </div>
      )}

      {/* Add Application Button */}
      {role === 'client_admin' && (
        <div className="flex justify-center my-8">
          <button
            onClick={openModal}
            className="bg-blue-600/20 border border-blue-400/60 text-blue-400 px-6 py-2.5 rounded-xl cursor-pointer hover:bg-blue-600/30 transition-colors font-medium"
          >
            Add Application
          </button>
        </div>
      )}

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-[#101012] border border-[#232325] rounded-2xl">
          <div className="flex flex-row justify-between items-center">
            <p className="text-white text-base sm:text-lg m-4 ml-6">What is Security Score trend?</p>
            <TrendingUp className="text-purple-400 w-7 h-7 m-4 flex-shrink-0" />
          </div>
          <div className="text-gray-300 text-sm px-6 pb-6 leading-relaxed">
            The Security Score Trend represents the progression of an application's overall security posture over time. It helps track improvements or regressions based on vulnerability findings, configurations, and remediation efforts.
          </div>
        </div>
        {role === 'client_admin' && (
          <div className="bg-[#101012] border border-[#232325] rounded-2xl">
            <div className="flex flex-row justify-between items-center">
              <p className="text-white text-base sm:text-lg m-4 ml-6">How to Create a New Scan</p>
              <TestTube className="text-yellow-400 w-7 h-7 m-4 flex-shrink-0" />
            </div>
            <div className="text-gray-300 text-sm px-6 pb-6 leading-relaxed">
              To initiate a scan, click the <strong className="text-white">"New Scan"</strong> button, configure your endpoint,
              then click <strong className="text-white">"Start Scan"</strong> to begin analysis.
            </div>
          </div>
        )}
        <div className="bg-[#101012] border border-[#232325] rounded-2xl">
          <div className="flex flex-row justify-between items-center">
            <p className="text-white text-base sm:text-lg m-4 ml-6">What is Security Score?</p>
            <Shield className="text-cyan-400 w-7 h-7 m-4 flex-shrink-0" />
          </div>
          <div className="text-gray-300 text-sm px-6 pb-6 leading-relaxed">
            Security Score is a value (0–100) that reflects how secure your endpoint is. It's based on:
            missing headers, CORS misconfigurations, SSL/TLS strength, and exposed auth mechanisms.
          </div>
        </div>
      </div>

      {/* Modal for Adding Application */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div
            className="bg-[#101012] border border-[#232325] text-white p-6 rounded-2xl shadow-2xl w-full max-w-md relative max-h-[90vh] overflow-y-auto"
            ref={modalRef}
          >
            <h2 className="text-xl font-semibold mb-4">Add New Application</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1 text-gray-300">Application Name</label>
                <input
                  type="text"
                  value={applicationName}
                  onChange={(e) => setApplicationName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#232325] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-[#232325] focus:border-blue-500 transition-colors"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-gray-300">Business Owner</label>
                <input
                  type="text"
                  value={businessOwner}
                  onChange={(e) => setBusinessOwner(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#232325] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-[#232325] focus:border-blue-500 transition-colors"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <Slider
                  label="Confidentiality Requirement (CR)"
                  value={confidentiality}
                  onChange={setConfidentiality}
                  description="Measures the importance of maintaining the confidentiality of the data processed by the AI system."
                />
              </div>
              <div>
                <Slider
                  label="Integrity Requirement (IR)"
                  value={integrity}
                  onChange={setIntegrity}
                  description="Measures the importance of maintaining the integrity of the data and the AI system's outputs."
                />
              </div>
              <div>
                <Slider
                  label="Availability Requirement (AR)"
                  value={availability}
                  onChange={setAvailability}
                  description="Measures the importance of ensuring the AI system's availability for its intended purpose."
                />
              </div>
              <div>
                <Slider
                  label="Societal Impact Requirement (SIR)"
                  value={societalImpact}
                  onChange={setSocietalImpact}
                  description="Measures the importance of mitigating potential negative societal impacts of the AI system."
                />
              </div>

              {/* User Authority Level Selection */}
              <div className="pt-2">
                <label className="block text-sm mb-2 font-medium text-gray-300">User Authority Level</label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="non-authenticated"
                      name="userAuthority"
                      value="non-authenticated"
                      checked={userAuthority === 'non-authenticated'}
                      onChange={(e) => setUserAuthority(e.target.value)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="non-authenticated" className="text-sm cursor-pointer text-gray-300">
                      Non-Authenticated User
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="authenticated"
                      name="userAuthority"
                      value="authenticated"
                      checked={userAuthority === 'authenticated'}
                      onChange={(e) => setUserAuthority(e.target.value)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="authenticated" className="text-sm cursor-pointer text-gray-300">
                      Authenticated User
                    </label>
                  </div>

                  {/* Privilege Level */}
                  {userAuthority === 'authenticated' && (
                    <div className="ml-7 mt-2 p-3 bg-[#0D0D0E] rounded-xl border border-[#232325]">
                      <p className="text-xs text-gray-400 mb-2">Privilege Level:</p>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            id="low-privilege"
                            name="privilegeLevel"
                            value="low"
                            checked={privilegeLevel === 'low'}
                            onChange={(e) => setPrivilegeLevel(e.target.value)}
                            className="w-4 h-4 accent-blue-500 cursor-pointer"
                            disabled={loading}
                          />
                          <label htmlFor="low-privilege" className="text-sm cursor-pointer text-gray-300">
                            Low Privilege User
                          </label>
                        </div>

                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            id="high-privilege"
                            name="privilegeLevel"
                            value="high"
                            checked={privilegeLevel === 'high'}
                            onChange={(e) => setPrivilegeLevel(e.target.value)}
                            className="w-4 h-4 accent-blue-500 cursor-pointer"
                            disabled={loading}
                          />
                          <label htmlFor="high-privilege" className="text-sm cursor-pointer text-gray-300">
                            High Privilege User
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Application Capabilities */}
              <div className="pt-2">
                <label className="block text-sm mb-2 font-medium text-gray-300">Application Capabilities</label>
                <p className="text-xs text-gray-500 mb-3">Indicate which AI integrations this application uses. These influence the scope of vulnerability impact.</p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="has-mcp"
                      checked={hasMcp}
                      onChange={(e) => setHasMcp(e.target.checked)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="has-mcp" className="flex items-center gap-2 text-sm cursor-pointer text-gray-300">
                      <Plug className="w-4 h-4 text-indigo-400" />
                      Uses Model Context Protocol (MCP)
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="has-agentic"
                      checked={hasAgenticWorkflow}
                      onChange={(e) => setHasAgenticWorkflow(e.target.checked)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="has-agentic" className="flex items-center gap-2 text-sm cursor-pointer text-gray-300">
                      <Bot className="w-4 h-4 text-purple-400" />
                      Uses Agentic Workflows
                    </label>
                  </div>
                </div>
              </div>

              {/* Network Exposure */}
              <div className="pt-2">
                <label className="block text-sm mb-2 font-medium text-gray-300">Network Exposure</label>
                <p className="text-xs text-gray-500 mb-3">How is this asset reachable? Drives the AIVSS Modified Attack Vector.</p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="ne-public"
                      name="networkExposure"
                      value="public"
                      checked={networkExposure === 'public'}
                      onChange={(e) => setNetworkExposure(e.target.value)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="ne-public" className="text-sm cursor-pointer text-gray-300">
                      Publicly accessible (internet-reachable)
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="ne-restricted"
                      name="networkExposure"
                      value="restricted"
                      checked={networkExposure === 'restricted'}
                      onChange={(e) => setNetworkExposure(e.target.value)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="ne-restricted" className="text-sm cursor-pointer text-gray-300">
                      IP-whitelisted / VPN / partner-restricted
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="ne-internal"
                      name="networkExposure"
                      value="internal"
                      checked={networkExposure === 'internal'}
                      onChange={(e) => setNetworkExposure(e.target.value)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="ne-internal" className="text-sm cursor-pointer text-gray-300">
                      Internal only (air-gapped / on-prem)
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row justify-end mt-6 gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="w-full sm:w-auto px-5 py-2.5 bg-[#232325] hover:bg-[#2e2e30] text-gray-300 rounded-lg transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                disabled={loading}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Editing Application */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div
            className="bg-[#101012] border border-[#232325] text-white p-6 rounded-2xl shadow-2xl w-full max-w-md relative max-h-[90vh] overflow-y-auto"
            ref={editModalRef}
          >
            <h2 className="text-xl font-semibold mb-4">Edit Application</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1 text-gray-300">Application Name</label>
                <input
                  type="text"
                  value={editAssetName}
                  readOnly
                  disabled
                  className="w-full px-4 py-2.5 bg-[#1a1a1d] text-gray-400 rounded-lg border border-[#232325] cursor-not-allowed"
                  title="Application name cannot be changed"
                />
                <p className="text-xs text-gray-500 mt-1">Application name cannot be changed.</p>
              </div>
              <div>
                <label className="block text-sm mb-1 text-gray-300">Business Owner</label>
                <input
                  type="text"
                  value={editBusinessOwner}
                  onChange={(e) => setEditBusinessOwner(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#232325] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-[#232325] focus:border-blue-500 transition-colors"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <Slider
                  label="Confidentiality Requirement (CR)"
                  value={editConfidentiality}
                  onChange={setEditConfidentiality}
                  description="Measures the importance of maintaining the confidentiality of the data processed by the AI system."
                />
              </div>
              <div>
                <Slider
                  label="Integrity Requirement (IR)"
                  value={editIntegrity}
                  onChange={setEditIntegrity}
                  description="Measures the importance of maintaining the integrity of the data and the AI system's outputs."
                />
              </div>
              <div>
                <Slider
                  label="Availability Requirement (AR)"
                  value={editAvailability}
                  onChange={setEditAvailability}
                  description="Measures the importance of ensuring the AI system's availability for its intended purpose."
                />
              </div>
              <div>
                <Slider
                  label="Societal Impact Requirement (SIR)"
                  value={editSocietalImpact}
                  onChange={setEditSocietalImpact}
                  description="Measures the importance of mitigating potential negative societal impacts of the AI system."
                />
              </div>

              {/* User Authority Level */}
              <div className="pt-2">
                <label className="block text-sm mb-2 font-medium text-gray-300">User Authority Level</label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="edit-non-authenticated"
                      name="editUserAuthority"
                      value="non-authenticated"
                      checked={editUserAuthority === 'non-authenticated'}
                      onChange={(e) => setEditUserAuthority(e.target.value)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="edit-non-authenticated" className="text-sm cursor-pointer text-gray-300">
                      Non-Authenticated User
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="edit-authenticated"
                      name="editUserAuthority"
                      value="authenticated"
                      checked={editUserAuthority === 'authenticated'}
                      onChange={(e) => setEditUserAuthority(e.target.value)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="edit-authenticated" className="text-sm cursor-pointer text-gray-300">
                      Authenticated User
                    </label>
                  </div>

                  {editUserAuthority === 'authenticated' && (
                    <div className="ml-7 mt-2 p-3 bg-[#0D0D0E] rounded-xl border border-[#232325]">
                      <p className="text-xs text-gray-400 mb-2">Privilege Level:</p>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            id="edit-low-privilege"
                            name="editPrivilegeLevel"
                            value="low"
                            checked={editPrivilegeLevel === 'low'}
                            onChange={(e) => setEditPrivilegeLevel(e.target.value)}
                            className="w-4 h-4 accent-blue-500 cursor-pointer"
                            disabled={loading}
                          />
                          <label htmlFor="edit-low-privilege" className="text-sm cursor-pointer text-gray-300">
                            Low Privilege User
                          </label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            id="edit-high-privilege"
                            name="editPrivilegeLevel"
                            value="high"
                            checked={editPrivilegeLevel === 'high'}
                            onChange={(e) => setEditPrivilegeLevel(e.target.value)}
                            className="w-4 h-4 accent-blue-500 cursor-pointer"
                            disabled={loading}
                          />
                          <label htmlFor="edit-high-privilege" className="text-sm cursor-pointer text-gray-300">
                            High Privilege User
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Application Capabilities */}
              <div className="pt-2">
                <label className="block text-sm mb-2 font-medium text-gray-300">Application Capabilities</label>
                <p className="text-xs text-gray-500 mb-3">Indicate which AI integrations this application uses. These influence the scope of vulnerability impact.</p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="edit-has-mcp"
                      checked={editHasMcp}
                      onChange={(e) => setEditHasMcp(e.target.checked)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="edit-has-mcp" className="flex items-center gap-2 text-sm cursor-pointer text-gray-300">
                      <Plug className="w-4 h-4 text-indigo-400" />
                      Uses Model Context Protocol (MCP)
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="edit-has-agentic"
                      checked={editHasAgenticWorkflow}
                      onChange={(e) => setEditHasAgenticWorkflow(e.target.checked)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="edit-has-agentic" className="flex items-center gap-2 text-sm cursor-pointer text-gray-300">
                      <Bot className="w-4 h-4 text-purple-400" />
                      Uses Agentic Workflows
                    </label>
                  </div>
                </div>
              </div>

              {/* Network Exposure */}
              <div className="pt-2">
                <label className="block text-sm mb-2 font-medium text-gray-300">Network Exposure</label>
                <p className="text-xs text-gray-500 mb-3">How is this asset reachable? Drives the AIVSS Modified Attack Vector.</p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="edit-ne-public"
                      name="editNetworkExposure"
                      value="public"
                      checked={editNetworkExposure === 'public'}
                      onChange={(e) => setEditNetworkExposure(e.target.value)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="edit-ne-public" className="text-sm cursor-pointer text-gray-300">
                      Publicly accessible (internet-reachable)
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="edit-ne-restricted"
                      name="editNetworkExposure"
                      value="restricted"
                      checked={editNetworkExposure === 'restricted'}
                      onChange={(e) => setEditNetworkExposure(e.target.value)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="edit-ne-restricted" className="text-sm cursor-pointer text-gray-300">
                      IP-whitelisted / VPN / partner-restricted
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="edit-ne-internal"
                      name="editNetworkExposure"
                      value="internal"
                      checked={editNetworkExposure === 'internal'}
                      onChange={(e) => setEditNetworkExposure(e.target.value)}
                      className="w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={loading}
                    />
                    <label htmlFor="edit-ne-internal" className="text-sm cursor-pointer text-gray-300">
                      Internal only (air-gapped / on-prem)
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row justify-end mt-6 gap-3">
              <button
                onClick={() => setEditModalOpen(false)}
                className="w-full sm:w-auto px-5 py-2.5 bg-[#232325] hover:bg-[#2e2e30] text-gray-300 rounded-lg transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                disabled={loading}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationHomepage;
